/**
 * React Query hooks for Library Topic management
 * Public hooks for fetching topics, Admin hooks for CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@services/queryClient";
import * as libraryTopicService from "@services/libraryTopicApi";
import type {
  LibraryTopicListItem,
  LibraryTopicModel,
  CreateTopicRequest,
  UpdateTopicRequest,
  TopicStatsResponse,
} from "@/types/models";

// ============================================================================
// Public Hooks
// ============================================================================

/**
 * Hook to fetch all active library topics
 * Available to all authenticated users
 */
export function useLibraryTopics() {
  return useQuery<LibraryTopicListItem[], Error>({
    queryKey: queryKeys.library.topics(),
    queryFn: libraryTopicService.getLibraryTopics,
    staleTime: 1000 * 60 * 10, // Topics change rarely, cache for 10 minutes
  });
}

// ============================================================================
// Admin Hooks (requires super_admin role)
// ============================================================================

/**
 * Hook to fetch all library topics including inactive ones
 * Admin only - includes resource counts
 */
export function useAllLibraryTopics() {
  return useQuery<LibraryTopicModel[], Error>({
    queryKey: queryKeys.library.allTopics(),
    queryFn: libraryTopicService.getAllLibraryTopics,
  });
}

/**
 * Hook to fetch topic usage statistics
 * Admin only
 */
export function useTopicStats() {
  return useQuery<TopicStatsResponse, Error>({
    queryKey: queryKeys.library.topicStats(),
    queryFn: libraryTopicService.getTopicStats,
  });
}

/**
 * Hook to create a new library topic
 * Admin only
 */
export function useCreateTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTopicRequest) =>
      libraryTopicService.createLibraryTopic(data),
    onSuccess: () => {
      // Invalidate all topic queries to refresh lists
      queryClient.invalidateQueries({ queryKey: queryKeys.library.topics() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.library.allTopics(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.library.topicStats(),
      });
    },
  });
}

/**
 * Hook to update an existing library topic
 * Admin only
 */
export function useUpdateTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTopicRequest }) =>
      libraryTopicService.updateLibraryTopic(id, data),
    onSuccess: () => {
      // Invalidate all topic queries
      queryClient.invalidateQueries({ queryKey: queryKeys.library.topics() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.library.allTopics(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.library.topicStats(),
      });
    },
  });
}

/**
 * Hook to delete a library topic
 * Admin only
 * @param force If true, hard delete (only works if no resources use this topic)
 */
export function useDeleteTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, force = false }: { id: string; force?: boolean }) =>
      libraryTopicService.deleteLibraryTopic(id, force),
    onSuccess: () => {
      // Invalidate all topic queries
      queryClient.invalidateQueries({ queryKey: queryKeys.library.topics() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.library.allTopics(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.library.topicStats(),
      });
    },
  });
}

/**
 * Hook to reorder library topics
 * Admin only
 */
export function useReorderTopics() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (topicIds: string[]) =>
      libraryTopicService.reorderLibraryTopics(topicIds),
    onSuccess: () => {
      // Invalidate all topic queries
      queryClient.invalidateQueries({ queryKey: queryKeys.library.topics() });
      queryClient.invalidateQueries({
        queryKey: queryKeys.library.allTopics(),
      });
    },
  });
}

// Re-export types for convenience
export type { CreateTopicRequest, UpdateTopicRequest };
